import { Popover, Tooltip as SioTooltip } from '@systemeio/ui'
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Legend,
  LinearScale,
  ScatterDataPoint,
  Title,
  Tooltip,
} from 'chart.js'
import {
  EmailStatisticsChartAmountEnum,
  EmailStatisticsChartPercentEnum,
  emailStatisticsChartTitles,
} from 'modules/email-statistics/enums/email-statistics-chart-enum'
import { useEmailStatisticsCharts } from 'modules/email-statistics/hooks/use-email-statistics-charts'
import {
  EmailStatisticsChartDataType,
  EmailStatisticsFilterInterface,
} from 'modules/email-statistics/types/email-statistics-interface'
import { getEmailStatisticsChartOptions } from 'modules/email-statistics/utils/get-email-statistics-chart-options'
import React, { useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { TLocoType, useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { toShortDate } from 'shared/utils/date-time-format'
import { QuestionMarkIcon } from '@systemeio/ui/icons'
import { getCssRgbValue } from 'shared/utils/get-css-rgb-value'

interface EmailStatisticsChartsProps {
  filter: EmailStatisticsFilterInterface
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const EmailStatisticsCharts = ({ filter }: EmailStatisticsChartsProps) => {
  const { data: chartsData } = useEmailStatisticsCharts(filter)
  return (
    <div className={'flex flex-wrap gap-5'}>
      <EmailStatisticsChartItem
        isPreFetching={!chartsData}
        type={EmailStatisticsChartAmountEnum.sentAmount}
        total={chartsData?.totalSent}
        showGrid={chartsData?.totalSent !== 0}
        chartStats={chartsData?.byDayStats}
      />
      <EmailStatisticsChartItem
        isPreFetching={!chartsData}
        type={EmailStatisticsChartPercentEnum.openedPercent}
        total={chartsData?.averageStats.openedPercent}
        showGrid={chartsData?.averageStats.openedPercent !== 0}
        chartStats={chartsData?.byDayStats}
      />
      <EmailStatisticsChartItem
        isPreFetching={!chartsData}
        type={EmailStatisticsChartPercentEnum.spamReportsPercent}
        total={chartsData?.averageStats.spamReportsPercent}
        showGrid={chartsData?.averageStats.spamReportsPercent !== 0}
        chartStats={chartsData?.byDayStats}
      />
      <EmailStatisticsChartItem
        isPreFetching={!chartsData}
        type={EmailStatisticsChartPercentEnum.bouncedPercent}
        total={chartsData?.averageStats.bouncedPercent}
        showGrid={chartsData?.averageStats.bouncedPercent !== 0}
        chartStats={chartsData?.byDayStats}
      />
    </div>
  )
}

interface EmailStatisticsChartItemProps {
  isPreFetching: boolean
  type: EmailStatisticsChartAmountEnum | EmailStatisticsChartPercentEnum
  total?: number
  showGrid: boolean
  chartStats?: Record<string, EmailStatisticsChartDataType>
}

const EmailStatisticsChartItem = ({
  type,
  showGrid,
  total,
  isPreFetching,
  chartStats,
}: EmailStatisticsChartItemProps) => {
  const { t } = useLocoTranslation()
  const { intlLocale } = useUser()
  const [data, setData] = useState<ChartData<'bar', (number | ScatterDataPoint | null)[], string>>({
    labels: [],
    datasets: [],
  })

  useEffect(() => {
    if (chartStats) {
      setData({
        labels: Object.keys(chartStats).map(el => toShortDate(el, intlLocale)),
        datasets: [
          {
            data: Object.values(chartStats).map(el => el[type]),
            backgroundColor: getCssRgbValue('--brand-primary'),
            borderRadius: 2,
          },
        ],
      })
    }
  }, [chartStats, type, intlLocale])

  return (
    <div className={'flex w-full flex-col gap-5 lg:w-[calc(50%-10px)] lg:flex-row'}>
      <div
        className={
          'bg-background-secondary flex h-fit w-full flex-col justify-between overflow-x-hidden rounded-lg'
        }
      >
        <div
          className={
            'text-text-primary bg-brand-primary-subtle flex items-center gap-1 px-6 py-4 text-[17px] font-bold'
          }
        >
          {getChartTitle(t, type, isPreFetching, total)}
        </div>
        <div className={'px-6 py-4'}>
          {!isPreFetching ? (
            <Bar options={getEmailStatisticsChartOptions(showGrid)} data={data} height={430} />
          ) : (
            <div
              className={'bg-skeleton-primary h-[430px] animate-pulse rounded-lg text-transparent '}
            >
              .
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const getChartTitle = (
  t: TLocoType,
  type: EmailStatisticsChartItemProps['type'],
  isPreFetching: boolean,
  total?: number,
) => {
  switch (type) {
    case EmailStatisticsChartAmountEnum.sentAmount: {
      return (
        <>
          <SioTooltip
            wrapperClassName={'truncate'}
            label={t(emailStatisticsChartTitles[type])}
            mode={'overflow'}
          >{`${t(emailStatisticsChartTitles[type])}:`}</SioTooltip>
          {!isPreFetching && total !== undefined ? (
            <span className={'text-brand-primary'}>{total}</span>
          ) : (
            <span className={'bg-skeleton-primary animate-pulse rounded-lg px-5 text-transparent'}>
              .
            </span>
          )}
        </>
      )
    }
    case EmailStatisticsChartPercentEnum.spamReportsPercent: {
      return (
        <div className={'flex w-full items-center gap-1.5'}>
          <SioTooltip
            wrapperClassName={'truncate'}
            label={t(emailStatisticsChartTitles[type])}
            mode={'overflow'}
          >{`${t(emailStatisticsChartTitles[type])}:`}</SioTooltip>
          {!isPreFetching && total !== undefined ? (
            <span className={'text-brand-primary'}>{`${total.toFixed(1)}%`}</span>
          ) : (
            <span className={'bg-skeleton-primary animate-pulse rounded-lg px-5 text-transparent'}>
              .
            </span>
          )}
          <Popover
            label={t('dashboard.email_statistics.spam_percent_description')}
            popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
          >
            {show => (
              <QuestionMarkIcon
                className={`${
                  show ? `fill-brand-primary` : 'fill-fill-primary'
                } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
              />
            )}
          </Popover>
        </div>
      )
    }
    default: {
      return (
        <>
          <SioTooltip
            wrapperClassName={'truncate'}
            label={t(emailStatisticsChartTitles[type])}
            mode={'overflow'}
          >{`${t(emailStatisticsChartTitles[type])}:`}</SioTooltip>
          {!isPreFetching && total !== undefined ? (
            <span className={'text-brand-primary'}>{`${total.toFixed(1)}%`}</span>
          ) : (
            <span className={'bg-skeleton-primary animate-pulse rounded-lg px-5 text-transparent'}>
              .
            </span>
          )}
        </>
      )
    }
  }
}

export default EmailStatisticsCharts
